import axiosInstance from "../api/axiosConfig";
import { getApiBaseUrl } from "../baseUrl";

const PATH_V1_CANDIDATO = "/candidatos";

/**
 * Faz upload do currículo em PDF do candidato
 */
export async function uploadCurriculo(
  candidatoId: number,
  arquivo: File
): Promise<any> {
  const apiUrl = getApiBaseUrl();

  // Validar se é PDF
  if (arquivo.type !== "application/pdf") {
    throw new Error("Apenas arquivos PDF são permitidos");
  }

  // Validar tamanho (10MB)
  if (arquivo.size > 10 * 1024 * 1024) {
    throw new Error("Arquivo muito grande. Máximo 10MB");
  }

  const arrayBuffer = await arquivo.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  return await axiosInstance.post(
    `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/upload-curriculo`,
    bytes,
    {
      headers: {
        "Content-Type": "application/octet-stream",
      },
    }
  );
}

/**
 * Baixa o currículo do candidato
 */
export async function downloadCurriculo(candidatoId: number): Promise<Blob> {
  const apiUrl = getApiBaseUrl();

  const response = await axiosInstance.get(
    `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/curriculo`,
    {
      responseType: "blob",
      headers: {
        'Accept': 'application/pdf',
      },
    }
  );

  return response.data;
}

/**
 * Verifica se o candidato possui currículo
 */
export async function verificarCurriculo(
  candidatoId: number
): Promise<boolean> {
  try {
    const apiUrl = getApiBaseUrl();
    await axiosInstance.get(
      `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/curriculo`,
      {
        responseType: "blob",
        headers: {
          'Accept': 'application/pdf',
        },
      }
    );
    return true;
  } catch (error: any) {
    // Se for 404 ou 406 (Not Acceptable), significa que não tem currículo
    if (error?.response?.status === 404 || error?.response?.status === 406) {
      return false;
    }
    // Para outros erros, também retorna false para não quebrar a aplicação
    console.warn("Erro ao verificar currículo:", error?.response?.status);
    return false;
  }
}

/**
 * Remove o currículo do candidato
 */
export async function deleteCurriculo(candidatoId: number): Promise<any> {
  const apiUrl = getApiBaseUrl();

  return await axiosInstance.delete(
    `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/curriculo`
  );
}

/**
 * Faz upload da foto do candidato
 */
export async function uploadFoto(
  candidatoId: number,
  arquivo: File
): Promise<any> {
  const apiUrl = getApiBaseUrl();

  // Validar se é imagem
  if (!arquivo.type.startsWith("image/")) {
    throw new Error("Apenas arquivos de imagem são permitidos");
  }

  // Validar tamanho (5MB)
  if (arquivo.size > 5 * 1024 * 1024) {
    throw new Error("Arquivo muito grande. Máximo 5MB");
  }

  const arrayBuffer = await arquivo.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);

  return await axiosInstance.post(
    `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/upload-foto`,
    bytes,
    {
      headers: {
        "Content-Type": "application/octet-stream",
      },
    }
  );
}

/**
 * Obtém a URL da foto do candidato
 */
export function getFotoUrl(candidatoId: number): string {
  const apiUrl = getApiBaseUrl();
  return `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/foto`;
}

/**
 * Baixa a foto do candidato e devolve uma object URL para exibição.
 * A rota GET /candidatos/{id}/foto exige JWT, então usar a URL direto
 * em <img src> resulta em 401 — é preciso baixar via axios (com token).
 */
export async function baixarFotoObjectUrl(
  candidatoId: number
): Promise<string | null> {
  try {
    const apiUrl = getApiBaseUrl();
    const response = await axiosInstance.get(
      `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/foto`,
      {
        responseType: "blob",
        headers: {
          'Accept': 'image/*',
        },
      }
    );
    return URL.createObjectURL(response.data);
  } catch (error: any) {
    // 404/406 significam que não tem foto; outros erros também não quebram a tela
    if (error?.response?.status !== 404 && error?.response?.status !== 406) {
      console.warn("Erro ao carregar foto:", error?.response?.status);
    }
    return null;
  }
}

/**
 * Verifica se o candidato possui foto
 */
export async function verificarFoto(
  candidatoId: number
): Promise<boolean> {
  try {
    const apiUrl = getApiBaseUrl();
    await axiosInstance.get(
      `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/foto`,
      {
        responseType: "blob",
        headers: {
          'Accept': 'image/*',
        },
      }
    );
    return true;
  } catch (error: any) {
    // Se for 404 ou 406 (Not Acceptable), significa que não tem foto
    if (error?.response?.status === 404 || error?.response?.status === 406) {
      return false;
    }
    // Para outros erros, também retorna false para não quebrar a aplicação
    console.warn("Erro ao verificar foto:", error?.response?.status);
    return false;
  }
}

/**
 * Remove a foto do candidato
 */
export async function deleteFoto(candidatoId: number): Promise<any> {
  const apiUrl = getApiBaseUrl();

  return await axiosInstance.delete(
    `${apiUrl}${PATH_V1_CANDIDATO}/${candidatoId}/foto`
  );
}

